import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  FolderOpen,
  LayoutGrid,
  MapPin,
  MessageSquare,
} from 'lucide-react';
import AppHeader from '../components/ui/AppHeader';
import ArchitectHeader from '../components/pm/ArchitectHeader';
import StaffPortalViewAs from '../components/pm/StaffPortalViewAs';
import GroupCommentsPopout from '../components/pm/GroupCommentsPopout';
import { ProjectUploadModalProvider } from '../components/pm/ProjectUploadModalProvider';
import { useAuth } from '../utils/AuthContext';
import { useCRM, useCRMCollections } from '../utils/CRMContext';
import {
  architectDesignCommentScopesFromProjects,
  useArchitectDesignComments,
} from '../hooks/useArchitectDesignComments';
import { listArchitects, projectHasArchitectUid } from '../utils/architectProjectAccess';
import {
  buildArchitectPortalPreviewSearch,
  parseArchitectPreviewUid,
} from '../utils/architectPortalPreviewQuery';
import {
  staffPortalPreviewExitHref,
  staffPortalPreviewHref,
} from '../utils/staffPortalPreview';
import { isDesignGroupName } from '../utils/designGroupName';
import {
  aggregateGroupComments,
  buildGroupMemberTasks,
  collectGroupCommentContext,
  type GroupCommentContext,
} from '../utils/groupComments';
import { getPortalProjectCardTitle } from '../utils/projectName';
import { subscribeToAnnotationsForProjectIds } from '../utils/pdfAnnotationService';
import {
  markArchitectDiscussionAsRead,
  markGroupAsRead,
  subscribeToReadReceipts,
  type ReadReceiptMap,
} from '../utils/taskReadReceipts';
import { canPreviewPartnerPortals, isArchitectRole, isCrmTestUser, isDesignerRole } from '../utils/userRoles';
import { architectDiscussionUnreadCount, filterArchitectDecisionComments } from '../utils/commentAudience';
import { resolveArchitectPortalDiscussionDeepLink } from '../utils/architectPortalDiscussionDeepLink';
import type { EnhancedDeal, PdfAnnotation, ProjectTask, TaskComment, TaskGroup, TimberProject } from '../types';
import './ArchitectPortal.css';

interface ProjectGroup {
  project: TimberProject;
  deal: EnhancedDeal | undefined;
}

const RECENT_DISCUSSION_LIMIT = 3;

function stripCommentHtml(html: string): string {
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function commentPreviewText(c: TaskComment): string {
  const plain = (c.body || '').trim() || stripCommentHtml(c.bodyHtml || '');
  if (plain.length <= 240) return plain;
  return `${plain.slice(0, 237)}…`;
}

function commentCreatedAtMs(c: TaskComment): number {
  const d = c.createdAt instanceof Date ? c.createdAt : new Date(c.createdAt as string);
  const t = d.getTime();
  return Number.isNaN(t) ? 0 : t;
}

function recentArchitectDiscussionComments(
  project: TimberProject,
  allComments: TaskComment[],
  groups: TaskGroup[],
  projects: TimberProject[],
  tasks: ProjectTask[],
  limit: number,
): TaskComment[] {
  if (!project.id) return [];
  const designGroup = groups.find((g) => g.projectId === project.id && isDesignGroupName(g.name));
  if (!designGroup) return [];
  const ctx = collectGroupCommentContext(designGroup, project, projects, groups, tasks, []);
  const thread = filterArchitectDecisionComments(aggregateGroupComments(allComments, ctx));
  return [...thread]
    .sort((a, b) => commentCreatedAtMs(b) - commentCreatedAtMs(a))
    .slice(0, limit);
}

const ExternalArchitectPortal: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    timberProjects,
    enhancedDeals,
    taskGroups,
    projectTasks,
    taskComments,
    crmUsers,
    loading: crmLoading,
    architectHubReady,
  } = useCRM();
  const pmCollectionsReady = useCRMCollections(['projectTasks', 'taskGroups', 'taskComments']);

  const realIsArchitect = isArchitectRole(user?.role);
  // Real architects hydrate timberProjects from users.architectHubProjectIds *after*
  // CRM `loading` flips false (empty [] meanwhile). Staff preview uses org-wide CRM.
  const projectsReady = realIsArchitect ? architectHubReady : !crmLoading;
  const previewUid = !realIsArchitect && canPreviewPartnerPortals(user?.role)
    ? parseArchitectPreviewUid(searchParams)
    : null;
  const architectUid = realIsArchitect ? user?.id : previewUid;
  const linkSearch = previewUid ? buildArchitectPortalPreviewSearch(previewUid) : '';
  const previewUser = previewUid ? crmUsers.find((u) => u.uid === previewUid) : null;
  const partnerArchitects = useMemo(() => listArchitects(crmUsers), [crmUsers]);
  const designers = useMemo(
    () => crmUsers.filter((u) => isDesignerRole(u.role) && u.isActive && !isCrmTestUser(u)),
    [crmUsers],
  );
  const engineers = useMemo(
    () => crmUsers.filter((u) => u.role === 'engineer' && u.isActive && !isCrmTestUser(u)),
    [crmUsers],
  );
  const canStaffPreview = canPreviewPartnerPortals(user?.role) && !realIsArchitect;
  const commentsReadOnly = !!previewUid;
  const realUserId = user?.id || '';

  const [popoutCommentsToken, setPopoutCommentsToken] = useState<number | null>(null);
  const [groupCommentsContext, setGroupCommentsContext] = useState<GroupCommentContext | null>(null);
  const [groupCommentsAnnotations, setGroupCommentsAnnotations] = useState<PdfAnnotation[]>([]);
  const [readReceipts, setReadReceipts] = useState<ReadReceiptMap>({});
  const [highlightPortalCommentId, setHighlightPortalCommentId] = useState<string | null>(null);
  const [portalDeepLinkConsumed, setPortalDeepLinkConsumed] = useState(false);
  const [portalDeepLinkError, setPortalDeepLinkError] = useState<string | null>(null);

  useEffect(() => {
    if (!realUserId) return;
    return subscribeToReadReceipts(realUserId, setReadReceipts);
  }, [realUserId]);

  const grantedProjects = useMemo(() => {
    if (!architectUid) return [];
    const dealsById = new Map(enhancedDeals.filter((d) => d.id).map((d) => [d.id!, d]));
    return timberProjects.filter((project) => {
      if (!project.id || project.status === 'completed') return false;
      const deal = project.dealId ? dealsById.get(project.dealId) : undefined;
      return projectHasArchitectUid(project, deal, architectUid);
    });
  }, [architectUid, timberProjects, enhancedDeals]);

  // Status cards need Design discussion previews without opening Project Hub.
  // Real architects: subscribe with the same decisionOnly query Hub uses.
  // Staff "view as architect": org-wide CRM taskComments already has the thread.
  const statusDesignScopes = useMemo(
    () => (realIsArchitect ? architectDesignCommentScopesFromProjects(grantedProjects) : []),
    [realIsArchitect, grantedProjects],
  );
  const architectStatusComments = useArchitectDesignComments(statusDesignScopes, realIsArchitect);
  const statusDiscussionComments = realIsArchitect ? architectStatusComments : taskComments;

  const projectGroups = useMemo(() => {
    const dealsById = new Map(enhancedDeals.filter((d) => d.id).map((d) => [d.id!, d]));
    const groups: ProjectGroup[] = [];
    for (const project of grantedProjects) {
      if (!project.id) continue;
      const deal = project.dealId ? dealsById.get(project.dealId) : undefined;
      groups.push({ project, deal });
    }
    groups.sort((a, b) => (a.project.name || '').localeCompare(b.project.name || ''));
    return groups;
  }, [grantedProjects, enhancedDeals]);

  const teamMembers = useMemo(
    () => crmUsers.filter((u) =>
      !isCrmTestUser(u)
      && (u.role === 'employee' || u.role === 'admin' || isDesignerRole(u.role) || u.role === 'engineer'),
    ),
    [crmUsers],
  );

  const switchPreview = (uid: string) => {
    navigate(uid ? `/architect-portal${buildArchitectPortalPreviewSearch(uid)}` : staffPortalPreviewExitHref());
  };

  const hubHref = (projectId: string, tab?: 'files') => {
    const params = new URLSearchParams(linkSearch.startsWith('?') ? linkSearch.slice(1) : linkSearch);
    if (tab) params.set('tab', tab);
    const q = params.toString();
    return `/project-hub/${projectId}${q ? `?${q}` : ''}`;
  };

  const handleOpenProjectDesignDecisions = useCallback((project: TimberProject): boolean => {
    if (!project.id) return false;
    const designGroup = taskGroups.find((g) => g.projectId === project.id && isDesignGroupName(g.name));
    if (!designGroup) return false;
    const ctx = collectGroupCommentContext(
      designGroup,
      project,
      timberProjects,
      taskGroups,
      projectTasks,
      [],
    );
    setGroupCommentsContext(ctx);
    if (realUserId) {
      void markGroupAsRead(realUserId, ctx.groupKey);
      void markArchitectDiscussionAsRead(realUserId, ctx.groupKey);
    }
    setPopoutCommentsToken(Date.now());
    return true;
  }, [timberProjects, taskGroups, projectTasks, realUserId]);

  useEffect(() => {
    const pid = groupCommentsContext?.primaryProjectId;
    if (!pid) {
      setGroupCommentsAnnotations([]);
      return;
    }
    return subscribeToAnnotationsForProjectIds(
      [pid],
      (byProject) => {
        setGroupCommentsAnnotations(byProject.get(pid) ?? []);
      },
      { decisionOnly: true },
    );
  }, [groupCommentsContext?.primaryProjectId]);

  const clearDeepLinkParams = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('project');
      next.delete('taskComment');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const onHighlightPortalCommentConsumed = useCallback(() => {
    setHighlightPortalCommentId(null);
  }, []);

  useEffect(() => {
    if (portalDeepLinkConsumed) return;
    const pid = searchParams.get('project')?.trim();
    const cid = searchParams.get('taskComment')?.trim();
    if (!pid) return;

    const resolution = resolveArchitectPortalDiscussionDeepLink({
      projectId: pid,
      commentId: cid,
      architectUid,
      // Same project set/id field the Status list uses (`timberProjects` → grantedProjects).
      timberProjects,
      enhancedDeals,
      taskGroups,
      projectsReady,
      taskGroupsReady: pmCollectionsReady,
    });

    if (resolution.status === 'idle' || resolution.status === 'pending') {
      // Still loading — clear a stale not_found banner if projects are arriving.
      setPortalDeepLinkError((prev) => (prev ? null : prev));
      return;
    }

    if (resolution.status === 'unavailable') {
      setPortalDeepLinkError((prev) =>
        prev === resolution.message ? prev : resolution.message,
      );
      // not_found can be wrong if hub ready raced; keep params and retry when
      // timberProjects updates. Other reasons are definitive once ready.
      if (resolution.reason === 'not_found') return;
      setPortalDeepLinkConsumed(true);
      clearDeepLinkParams();
      return;
    }

    // Only strip params after the Design discussion actually opens.
    const opened = handleOpenProjectDesignDecisions(resolution.project);
    if (!opened) return;
    if (resolution.commentId) setHighlightPortalCommentId(resolution.commentId);
    setPortalDeepLinkError(null);
    setPortalDeepLinkConsumed(true);
    clearDeepLinkParams();
  }, [
    architectUid,
    clearDeepLinkParams,
    enhancedDeals,
    handleOpenProjectDesignDecisions,
    pmCollectionsReady,
    portalDeepLinkConsumed,
    projectsReady,
    searchParams,
    taskGroups,
    timberProjects,
  ]);

  const groupCommentMemberTasks = useMemo(() => {
    if (!groupCommentsContext) return [];
    return buildGroupMemberTasks(groupCommentsContext, projectTasks, timberProjects, taskGroups, []);
  }, [groupCommentsContext, projectTasks, timberProjects, taskGroups]);

  const commentsPopoutDealId = useMemo(() => {
    if (!groupCommentsContext) return undefined;
    return timberProjects.find((p) => p.id === groupCommentsContext.primaryProjectId)?.dealId;
  }, [groupCommentsContext, timberProjects]);

  return (
    <ProjectUploadModalProvider
      projectId={groupCommentsContext?.primaryProjectId ?? null}
      dealId={commentsPopoutDealId}
    >
      <div className="ap-portal ap-combined">
        {realIsArchitect ? (
          <ArchitectHeader
            homePath="/architect-portal"
            homeLinkLabel="Status"
            showStandup={false}
            showHours={false}
            linkSearch={linkSearch}
          />
        ) : (
          <AppHeader />
        )}
        <div className="ap-container">
          {canStaffPreview && (
            <div className="ap-view-as">
              <StaffPortalViewAs
                designers={designers}
                architects={partnerArchitects}
                engineers={engineers}
                current={previewUid ? { kind: 'architect', uid: previewUid } : null}
                currentName={previewUser?.displayName}
                onChange={(next) => navigate(staffPortalPreviewHref(next.kind, next.uid))}
                onExit={() => switchPreview('')}
              />
            </div>
          )}

          {portalDeepLinkError && (
            <div className="ap-deeplink-error" role="alert">
              <p>{portalDeepLinkError}</p>
              <button
                type="button"
                className="ap-deeplink-error-dismiss"
                onClick={() => setPortalDeepLinkError(null)}
              >
                Dismiss
              </button>
            </div>
          )}

          <div className="ap-project-list">
            {projectGroups.length === 0 ? (
              <div className="ap-empty">
                <FolderOpen size={32} />
                <p>
                  {architectUid
                    ? 'No granted Design projects yet. LCTF will add you on a job when it is time to review drawings.'
                    : 'Choose an architect to preview their granted projects.'}
                </p>
              </div>
            ) : (
              projectGroups.map(({ project, deal }) => {
                const designGroup = taskGroups.find((g) => g.projectId === project.id && isDesignGroupName(g.name));
                const designCtx = designGroup
                  ? collectGroupCommentContext(
                      designGroup,
                      project,
                      timberProjects,
                      taskGroups,
                      projectTasks,
                      [],
                    )
                  : null;
                const decisionsUnread = designCtx
                  ? architectDiscussionUnreadCount(
                      filterArchitectDecisionComments(aggregateGroupComments(statusDiscussionComments, designCtx)),
                      readReceipts,
                      designCtx.groupKey,
                      realUserId,
                    ) > 0
                  : false;
                const recentComments = recentArchitectDiscussionComments(
                  project,
                  statusDiscussionComments,
                  taskGroups,
                  timberProjects,
                  projectTasks,
                  RECENT_DISCUSSION_LIMIT,
                );
                return (
                  <div key={project.id} className="ap-project-card">
                    <div className="ap-project-header">
                      <div className="ap-project-header-left">
                        <div className="ap-project-info">
                          <div className="ap-project-name-row">
                            <h3 className="ap-project-name">{getPortalProjectCardTitle(project, deal)}</h3>
                          </div>
                          <div className="ap-project-meta">
                            {project.location && (
                              <span className="ap-project-location">
                                <MapPin size={11} /> {project.location}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="ap-project-header-right">
                        {designGroup && (
                          <button
                            type="button"
                            className={`ap-task-comments-btn${decisionsUnread ? ' ap-task-comments-btn--unread' : ''}`}
                            title={decisionsUnread ? 'New discussion' : 'Architect discussion'}
                            aria-label={decisionsUnread ? 'Open architect discussion — unread' : 'Open architect discussion'}
                            onClick={() => handleOpenProjectDesignDecisions(project)}
                          >
                            <MessageSquare size={14} strokeWidth={1.85} aria-hidden />
                            {decisionsUnread && <span className="ap-task-comments-dot" aria-hidden />}
                          </button>
                        )}
                        {deal?.leadId && deal.id && (
                          <button
                            type="button"
                            className="ap-task-files-btn"
                            title="Open inspiration board"
                            aria-label="Open inspiration board"
                            onClick={() => navigate(`/client/${deal.leadId}/project/${deal.id}/board${linkSearch}`, {
                              state: { from: '/architect-portal' },
                            })}
                          >
                            <LayoutGrid size={14} aria-hidden />
                          </button>
                        )}
                        <button
                          type="button"
                          className="ap-task-files-btn"
                          title="Open project files"
                          aria-label="Open project files"
                          onClick={() => navigate(hubHref(project.id!, 'files'))}
                        >
                          <FolderOpen size={14} aria-hidden />
                        </button>
                      </div>
                    </div>
                    <div className="ap-project-discussion">
                      <div className="ap-project-discussion-head">
                        <span className="ap-project-discussion-label">Architect discussion</span>
                        {designGroup && (
                          <button
                            type="button"
                            className="ap-project-discussion-all"
                            onClick={() => handleOpenProjectDesignDecisions(project)}
                          >
                            View all
                          </button>
                        )}
                      </div>
                      {recentComments.length === 0 ? (
                        <p className="ap-project-discussion-empty">No messages yet.</p>
                      ) : (
                        <ul className="ap-project-discussion-list">
                          {recentComments.map((c) => {
                            const created = c.createdAt instanceof Date ? c.createdAt : new Date(c.createdAt as string);
                            return (
                              <li key={c.id || `${c.authorId}-${commentCreatedAtMs(c)}`} className="ap-discussion-item">
                                <div className="ap-discussion-item-meta">
                                  <span className="ap-discussion-author">{c.authorName || 'Team'}</span>
                                  <span className="ap-discussion-time">
                                    {formatDistanceToNow(created, { addSuffix: true })}
                                  </span>
                                </div>
                                <p className="ap-discussion-snippet">{commentPreviewText(c)}</p>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <GroupCommentsPopout
          groupContext={groupCommentsContext}
          popoutToken={popoutCommentsToken}
          dealId={commentsPopoutDealId}
          projectPdfAnnotations={groupCommentsAnnotations}
          highlightCommentId={highlightPortalCommentId}
          onHighlightCommentConsumed={onHighlightPortalCommentConsumed}
          onClose={() => {
            setGroupCommentsContext(null);
            setPopoutCommentsToken(null);
            setHighlightPortalCommentId(null);
          }}
          groupMemberTasks={groupCommentMemberTasks}
          teamMembers={teamMembers}
          currentUserId={architectUid || realUserId}
          readOnly={commentsReadOnly}
          architectSurface
          initialTab="architect"
          onOpenPdf={(_u, _t, docId, opts) => {
            const pid = groupCommentsContext?.primaryProjectId;
            if (!pid) return;
            const params = new URLSearchParams(linkSearch.startsWith('?') ? linkSearch.slice(1) : linkSearch);
            params.set('tab', 'files');
            params.set('file', docId);
            if (opts?.annotationId) params.set('comment', opts.annotationId);
            navigate(`/project-hub/${pid}?${params.toString()}`);
          }}
          onOpenImage={(_u, _t, docId, opts) => {
            const pid = groupCommentsContext?.primaryProjectId;
            if (!pid) return;
            const params = new URLSearchParams(linkSearch.startsWith('?') ? linkSearch.slice(1) : linkSearch);
            params.set('tab', 'files');
            params.set('file', docId);
            if (opts?.annotationId) params.set('comment', opts.annotationId);
            navigate(`/project-hub/${pid}?${params.toString()}`);
          }}
        />
      </div>
    </ProjectUploadModalProvider>
  );
};

export default ExternalArchitectPortal;
